import {ActivityType, TJobTask} from './activity.types'
import {TMovement} from './movement.types'
import {TTrainStationWithDailyStatusAndNotes} from './trainStation.types'
import {TBuilding} from '../service/building.services'
import {TGeoMarker} from './geoMarker.types'
import {TProtectionZone} from './protectionZone.types'
import {TProtectionTypeWithModes} from './protectionType.types'
import type {TResourceRole} from './resource.type'
import {TSector as TNetworkSector} from './sector.type'
import type {TViewportWindow, TViewportTransit} from './viewport.type'
import {
  TActivityProgress,
  TReportOutcome,
  TShiftReportSummary,
} from './shiftReport.type'

export interface ProjectFormType {
  id?: string
  networkId?: string
  name: string
  acronym: string
  title: string
  day_start: string
  day_end: string
  night_start: string
  night_end: string
  tracks: VoieFormType[]
  echantillonValue: number
  submit_day: string
  submit_time: string
  kmStart: number
  kmEnd: number
}

export type TProjectListItem = {
  id: string
  networkId: string
  name: string
  acronym: string
  status: string
  kmStart: number
  kmEnd: number
  direction: string
  tracks?: {
    id: string
    name: string
    acronym: string
  }[]
}

export type TProject = {
  id: string
  networkId: string
  network?: {
    id?: string
    clientId: string
    name?: string
    acronym?: string | null
  }
  name: string
  acronym: string
  title: string
  dayStart: string
  dayEnd: string
  nightStart: string
  nightEnd: string
  echantillonValue: number
  submitDay: number
  submitTime: string
  kmStart: number
  kmEnd: number
  direction?: 'ASC' | 'DESC'
  status: string
  sampleValue?: number
  author?: string
  telAuthor?: string
  updateDate?: Date
  informationText?: string
  sectors: TNetworkSector[]
  tracks: TTrack[]
  subSectors: TProjectSector[]
  switches: TSwitch[]
  teams: TTeam[]
  protectionTypes: TProtectionTypeWithModes[]
  geoMarkers: TGeoMarker[]
  protectionZones: TProtectionZone[]
  pointAccesses: TPointAccess[]
  activities: TActivity[]
  movements: TMovement[]
  trainStations?: TTrainStationWithDailyStatusAndNotes[]
  buildings?: TBuilding[]
}

/**
 * The lean scope the network-native wrapper (Lot 3, Unit 2) shares with its
 * child views via Outlet context. Since the URL carries `:networkId` and the
 * whole rendering pipeline is sourced from the viewport, the views only need
 * the network id and the displayed PK extent — every other former `TProject`
 * field was project-only (shift times, echantillon, embedded geo arrays) and is
 * gone. `id` stays for the residual project-scoped writers that are migrated in
 * Unit 4; it is empty until then, which is what auto-hides the New Demand button.
 */
export type TNetworkViewData = {
  id: string
  networkId: string
  /** The active viewport window (PK range or sector) driving the week fetch. */
  window?: TViewportWindow
}

// Legacy project-scoped sector shape — used by subSectors.
// The new network-scoped Sector is TSector from sector.type.ts.
export type TProjectSector = {
  id: string
  projectId: string
  name: string
  acronym: string
  kmStart: number
  kmEnd: number
  startPoint?: number
  endPoint?: number
  width?: number
}

export type TTrack = {
  id: string
  projectId: string
  name: string
  acronym: string
  /**
   * Where this track actually exists, in PK (network label tracks only).
   *
   * A track may cover only part of the displayed window ("V3 exists on 2% of
   * the axis"). Present on the network-sourced lanes, absent on legacy project
   * tracks. Drives the greyed no-track zones and bounds drag/resize to the
   * segment the activity sits on.
   */
  coverage?: {kmStart: number; kmEnd: number}[]
  /** Pixel runs of the lane with no track, on the shared axis. */
  gaps?: {startPoint: number; width: number}[]
}

export interface VoieFormType {
  id: string
  name: string
  acronym: string
}

// Nature of a railway switch (aiguillage). 1 & 2 are the two simple-switch
// orientations (Montant / Descendant), 3 is a crossover. Labels live in
// SWITCH_TYPE (constants.ts). Mirrors SWITCH_TYPES in
// api/src/models/constants/switch.constants.ts — keep the values in sync.
export enum ESwitchType {
  SIMPLE_1 = 1,
  SIMPLE_2 = 2,
  DOUBLE = 3,
  // Straight through-connection between two points, network-scoped: it is not a
  // device an operator declares inside a project, so it stays out of SWITCH_TYPE.
  STRAIGHT = 4,
}

export type TSwitch = {
  id: string
  type: ESwitchType
  kmPoint: number
  isActive: boolean
  trackLabels?: [string, string]
}

export type TTeam = {
  id: string
  clientId: string
  name: string
  acronym: string
  color: string
  isActive: boolean
}

// Nature of a point access.
export enum EPointAccessType {
  LEVEL_CROSSING = 1, // passage à niveau
  PEDESTRIAN_ACCESS = 2, // accès piéton
  EMERGENCY_EXIT = 3, // sortie de secours
}

export type TPointAccess = {
  id: string
  projectId: string
  trackIds: string[]
  type: EPointAccessType
  name: string
  kmPoint: number
  isActive: boolean
}

export type TZoneInfo = {
  id: string
  type: GeoZoneType
  name?: string
}

export type GeoZoneType =
  | 'sector'
  | 'subSector'
  | 'geoMarker'
  | 'protectionZone'
  | 'trainStation'

export type TTrackInfo = {
  id: string
  name: string
  acronym: string
}

/**
 * Exactly one of networkTrackId / buildingId / projectId is set, depending on sectionType:
 *  - WORK_ACTIVITY / MOVEMENT_ACTIVITY → networkTrackId
 *  - BUILDING_ACCESS                   → buildingId (and section.building.projectId)
 *  - ENCLOSURE_ACCESS                  → projectId (direct reference)
 */
export type TActivitySection = {
  id: string
  activityId: string
  networkTrackId?: string | null
  projectId?: string | null
  kmStart: number | null
  kmEnd: number | null
  networkTrack?: {
    id: string
    name: string
    acronym: string
  }
  building?: {
    id: string
    name: string
    projectId: string
  }
}

export type TTrackRef = {id: string; name: string; acronym: string | null}

export type TActivity = {
  id: string
  activityGroupId: string
  activityType: ActivityType
  week: string
  year: string
  requestorName: string
  phoneNumber: string
  holderName: string
  holderPhoneNumber: string
  kmStart: number | null
  kmEnd: number | null
  kmEntrance?: string
  kmExit?: string
  description: string
  teamId: string
  company?: string
  shift: 'day' | 'night'
  date: string // Format YYYY-MM-DD
  startTime?: string // Format HH:mm
  endTime?: string // Format HH:mm
  status: string
  /** Set while status === 'cancelled': the cancellation notice responsible. */
  cancelledByNotice?: {id: string; name: string} | null
  comment: string
  priority?: number | null
  // Tracks resolved from the pad designated by kmEntrance/kmExit, when set.
  entryTracks?: TTrackRef[]
  exitTracks?: TTrackRef[]
  // Write-side scalars (ids), mirroring entryTracks/exitTracks, carried into
  // UpdateActivitySchema on edit.
  entryTrackIds?: string[]
  exitTrackIds?: string[]
  isLive: boolean
  licenseNumber: string
  docNumber?: string
  createdAt: Date
  updatedAt: Date
  createdById: string
  team: TTeam
  activitySections: TActivitySection[]
  zones?: TZoneInfo[]
  // Section updates for API (used when updating multi-track activities)
  sections?: Array<{
    id?: string
    // Addressing truth since Lot 3 §5.3 — must be carried through the update
    // remap or the section reaches the API with no spatial key. Every
    // persisted section already has one (tightening lot); no legacy trackId.
    networkTrackId?: string | null
    buildingId?: string
    kmStart: number | null
    kmEnd: number | null
  }>
  protections?: Array<{
    id?: string
    protectionZoneId: string
    protectionModeId?: string
    comment?: string
    // Enriched fields returned by the API (not required for creation)
    protectionZoneName?: string
    protectionType?: string
    protectionModeName?: string | null
    icon?: string
    color?: string
    zoneColor?: string | null
  }>
  protectionComment?: string
  /**
   * Where the job stands before and after this date. On the by-id activity
   * read only; absent from list responses.
   */
  progress?: TActivityProgress
  /** This date's report without its texts. By-id read only; null when none. */
  report?: TShiftReportSummary | null
  /** Whether the requesting user may write this date's report. */
  canReport?: boolean
  /** When this date was last reported (ISO), null when never. List payloads only. */
  reportedAt?: string | null
  /** How the shift went, null when not reported. List payloads only. */
  outcome?: TReportOutcome | null
  /**
   * The job scope. Present on the activity read (a solo activity's own tasks,
   * or its group's); absent on list responses.
   */
  tasks?: TJobTask[]
  // Resource holder links (Lot 2). Derived display via getActivityHolders;
  // legacy holderName/holderPhoneNumber remain the fallback when empty.
  resourceLinks?: Array<{
    resource: {
      id: string
      firstName: string
      lastName: string
      phone?: string | null
      contact?: string | null
      roles?: TResourceRole[]
    }
  }>
  /**
   * Derived TRANSIT bands for this WORK activity (its travel to/from the work
   * zone), shipped by the week viewport peeled out of `activitySections` so they
   * never render as editable work. Drawn glued to this activity. Absent outside
   * the week viewport (e.g. paginated list responses).
   */
  transitSections?: TViewportTransit[]
}

// Enhanced activity type from the new /api/client/:clientId/activities endpoint
export type TActivityEnhanced = TActivity & {
  track: TTrackInfo
  canEdit: boolean
  canDelete: boolean
}

// Pagination response for activities endpoint
export type TActivitiesResponse = {
  data: TActivityEnhanced[]
  total: number
  page: number
  perPage: number
}

// Filters for the network-scoped activities endpoint (/api/network/:networkId/activities).
export type TNetworkActivitiesFilters = {
  date?: string // YYYY-MM-DD
  from?: string
  to?: string
  weekNumber?: number
  year?: number
  trackIds?: string[]
  shift?: 'day' | 'night'
  teamIds?: string[]
  requestorIds?: string[]
  activityTypes?: string[]
  statuses?: string[]
  sectorIds?: string[]
  protectionModeIds?: string[]
  sortBy?:
    | 'status'
    | 'date'
    | 'shift'
    | 'project'
    | 'track'
    | 'kmStart'
    | 'kmEnd'
    | 'team'
    | 'activityType'
    | 'requestorName'
    | 'description'
    | 'priority'
    | 'protections'
  sortOrder?: 'asc' | 'desc'
  // Only meaningful when sortBy === 'protections': order activities by the zone
  // name of their protections belonging to this protection type.
  sortProtectionTypeId?: string
  search?: string
  entry?: string
}

export type TApproval = {
  id: string
  projectId: string
  ApprovedDate: string
  ApprovedById: string
  createdAt: Date
  deletedAt: Date
  deletedById: string
}

/**
 * Row of the network junction list — `GET /api/network/:networkId/junctions`.
 * A junction is addressed by (networkId, kmA, kmB): two network PK positions it
 * links. The legacy project pair is dropped from the read since the soft-kill.
 */
export type TProjectJunction = {
  id: string
  kmA: number
  kmB: number
  networkId: string | null
}
